// @ts-nocheck
/**
 * Cloud Telephony Users — zod schemas for Create + Update forms.
 *
 * Field set + validation rules mirror the byte-identical v1 UserAddPage
 * catalog exactly; only presentation changes. See [[project_ct_v1_source_branch]]
 * for the v1 source of truth.
 *
 * Cross-field rules:
 *   • password === confirmPassword
 *   • company required when target ∈ {RESELLER, COMPANY_ADMIN}
 *   • callCentreId required when target === CALL_CENTRE_ADMIN
 *   • aclSettings required when target ≠ CALL_CENTRE_AGENT
 *   • expiryDate ≥ today (and ≤ parentEffectiveMax, enforced at form level)
 *   • email OTP token required when creatorRequiresEmailVerification
 *
 * Not carried over (dead in v1):
 *   • capacity fields (channel/agents/team_leads/live_agents) — never sent
 *   • duplicate `signup_email_token` inclusion — collapsed to one
 */
import { z } from 'zod';
import { USER_ROLES } from '@/utilities/constant';
import { isCallCentreUserRole, parseAgentLeadsInput } from '@/utilities/agentLeadsQuota';

/* ── Reusable building blocks ─────────────────────────────────────── */

/**
 * v1 password rules — mirrors utilities/userCreation.js exactly:
 *   ≥8 chars, ≥1 upper, ≥1 lower, ≥1 digit, ≥1 special from a specific
 *   15-char set, no whitespace. The special-char set is narrower than
 *   what an intuition guess would include — `_`, `-`, `[`, `]`, `/`,
 *   `+`, `=`, `~`, `` ` `` do NOT count — so this must match the
 *   backend regex verbatim or client-passing passwords get 400'd on
 *   POST /api/obd/user/signup.
 * v1 source: utilities/userCreation.js:23  PASSWORD_SPECIAL_CHAR_REGEX
 */
const PASSWORD_SPECIAL_CHAR_REGEX = /[!@#$%^&*(),.?":{}|<>]/;

export const strongPassword = z
  .string()
  .min(8, 'Use at least 8 characters')
  .max(100, 'Too long (max 100)')
  .regex(/[A-Z]/, 'Include at least one uppercase letter')
  .regex(/[a-z]/, 'Include at least one lowercase letter')
  .regex(/[0-9]/, 'Include at least one number')
  .regex(PASSWORD_SPECIAL_CHAR_REGEX, 'Include at least one special character (! @ # $ % ^ & * ( ) , . ? " : { } | < >)')
  .refine((v) => !/\s/.test(v), 'No spaces allowed');

/** Username: 3–50 chars, alphanumerics + underscore + space. */
export const usernameField = z
  .string()
  .min(3, 'At least 3 characters')
  .max(50, 'Too long (max 50)')
  .regex(/^[a-zA-Z0-9_ ]+$/, 'Only letters, numbers, underscore, and space');

/** Full name: 2–100 chars, same charset as username. */
export const nameField = z
  .string()
  .min(2, 'At least 2 characters')
  .max(100, 'Too long (max 100)')
  .regex(/^[a-zA-Z0-9_ ]+$/, 'Only letters, numbers, underscore, and space');

export const emailField = z
  .string()
  .min(1, 'Email is required')
  .max(100, 'Too long (max 100)')
  .email('Enter a valid email');

/** Expiry: must be a real future date. Upper bound enforced at form level from parent. */
export const expiryDateField = z
  .string()
  .min(1, 'Pick an expiry date')
  .refine((v) => {
    const d = new Date(v);
    if (!Number.isFinite(d.getTime())) return false;
    d.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return d.getTime() >= today.getTime();
  }, 'Expiry must be today or later');

/** Base object — every field a Create User form can carry. */
const userCreateBase = z.object({
  /* Step 1 — Identity & Role */
  username: usernameField,
  name: nameField,
  role: z.string().min(1, 'Pick a role'), // one of USER_ROLES.* — validated at form level from creator gating

  /* Step 2 — Email & Verification (OTP token filled by the verifier component) */
  email: emailField,
  signupEmailToken: z.string().optional(),

  /* Step 3 — Credentials & Expiry */
  password: strongPassword,
  confirmPassword: z.string().min(1, 'Confirm the password'),
  expiryDate: expiryDateField,

  /* Step 4 — Access & Assignment */
  aclSettings: z.string().optional(),
  accountType: z.enum(['0', '1'], { errorMap: () => ({ message: 'Pick an account type' }) }),
  company: z.string().optional(),
  callCentreId: z.string().optional(),
  agentLeads: z.string().optional(),

});

/* Role sets used by cross-field refinements. */
const REQUIRES_COMPANY = new Set<string>([USER_ROLES.RESELLER, USER_ROLES.COMPANY_ADMIN]);
const REQUIRES_CALL_CENTRE = new Set<string>([USER_ROLES.CALL_CENTRE_ADMIN]);
const SKIPS_ACL = new Set<string>([USER_ROLES.CALL_CENTRE_AGENT]);

/** Cross-field rules shared between create + update. */
function applyCrossFieldRules<T extends z.ZodTypeAny>(schema: T): T {
  return schema
    .refine((d: z.infer<T>) => !REQUIRES_COMPANY.has(d.role) || Boolean(d.company), {
      message: 'Pick a company',
      path: ['company'],
    })
    .refine((d: z.infer<T>) => !REQUIRES_CALL_CENTRE.has(d.role) || Boolean(d.callCentreId), {
      message: 'Pick a call centre',
      path: ['callCentreId'],
    })
    .refine((d: z.infer<T>) => SKIPS_ACL.has(d.role) || Boolean(d.aclSettings), {
      message: 'Pick an ACL setting',
      path: ['aclSettings'],
    })
    .refine((d: z.infer<T>) => {
      if (!isCallCentreUserRole(d.role)) return true;
      if (d.agentLeads === undefined || d.agentLeads === null || String(d.agentLeads).trim() === '') return true;
      return parseAgentLeadsInput(d.agentLeads).ok;
    }, {
      message: 'Agent leads must be a whole number from 0 to 1000000',
      path: ['agentLeads'],
    }) as unknown as T;
}

export const createUserSchema = applyCrossFieldRules(
  userCreateBase.refine((d) => d.password === d.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  }),
);
export type CreateUserInput = z.infer<typeof createUserSchema>;

/** Update variant — same fields minus credentials block. */
export const updateUserSchema = applyCrossFieldRules(
  userCreateBase.omit({ username: true, password: true, confirmPassword: true, signupEmailToken: true }),
);
export type UpdateUserInput = z.infer<typeof updateUserSchema>;

/* ── Per-step field arrays — drive validation when user clicks Next ── */

// Step 0 merges the old Identity + Email + Credentials blocks so the wizard
// is 4 clicks (Account → Access → Branding → Review) instead of 6. Email OTP
// gating still runs from the page (page-level check).
export const STEP_FIELDS: Record<number, Array<keyof CreateUserInput>> = {
  0: ['username', 'name', 'role', 'email', 'password', 'confirmPassword'],
  1: ['aclSettings', 'accountType', 'company', 'callCentreId', 'expiryDate', 'agentLeads'],
  2: [], // Review
};

/* ── Helpers used by the form + submit ────────────────────────────── */

export function isCompanyRequired(role: string) {
  return REQUIRES_COMPANY.has(role);
}
export function isCallCentreRequired(role: string) {
  return REQUIRES_CALL_CENTRE.has(role);
}
export function isAclRequired(role: string) {
  return Boolean(role) && !SKIPS_ACL.has(role);
}
